import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  MessageCircle, Send, RefreshCcw, Loader2, CheckCircle2, CheckCheck, AlertTriangle,
  Eye, Clock, XCircle, RotateCw, History, ChevronDown, ChevronUp, ShieldCheck, Hourglass,
} from "lucide-react";

const STATUS_META = {
  queued: { icon: Clock, color: "#9A3412", label: "In coda" },
  sending: { icon: Loader2, color: "#1E40AF", label: "Invio…", spin: true },
  sent: { icon: CheckCircle2, color: "#1E40AF", label: "Inviato" },
  delivered: { icon: CheckCheck, color: "#065F46", label: "Consegnato" },
  read: { icon: Eye, color: "#065F46", label: "Letto" },
  failed: { icon: AlertTriangle, color: "#991B1B", label: "Errore" },
  undelivered: { icon: XCircle, color: "#991B1B", label: "Non consegnato" },
  received: { icon: MessageCircle, color: "#1B5E20", label: "Ricevuto" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: meta.color }} data-testid={`wa-status-${status}`}>
      <Icon className={`w-3.5 h-3.5 ${meta.spin ? "animate-spin" : ""}`} /> {meta.label}
    </span>
  );
}

export default function WhatsappPanel({ cid, cliente, pianificazione, autoTemplate, onAutoTemplateHandled }) {
  const { user } = useAuth();
  const canSend = user?.role === "admin" || user?.role === "operatore";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [windowInfo, setWindowInfo] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);  // {template, body, to_number, mocked, ...}
  const [showHistory, setShowHistory] = useState(true);
  const [followupMinutes, setFollowupMinutes] = useState(60); // default: 1h
  const threadRef = useRef(null);

  const hasVariazione = !!(pianificazione?.data_inizio_prev && pianificazione?.data_inizio);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [msgs, s, win] = await Promise.all([
        api.get(`/clienti/${cid}/whatsapp/messages`),
        api.get("/settings/whatsapp").catch(() => ({ data: null })),
        api.get(`/clienti/${cid}/whatsapp/window`).catch(() => ({ data: null })),
      ]);
      setMessages(msgs.data || []);
      if (s.data) setSettings(s.data);
      setWindowInfo(win.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  // Deep-link da Squadre.jsx: dopo un drag&drop il toast può portare qui con ?wa=variazione
  useEffect(() => {
    if (!autoTemplate) return;
    if (!cliente?.telefono) return; // niente preview senza numero
    if (autoTemplate === "variazione" && !hasVariazione) return; // nessuna variazione disponibile
    requestPreview(autoTemplate);
    onAutoTemplateHandled && onAutoTemplateHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTemplate, cliente?.telefono, hasVariazione]);

  // Auto-refresh ogni 6s per ricevere le risposte cliente / status delivery
  useEffect(() => {
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [load]);

  // Scroll thread in fondo all'apertura
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const requestPreview = async (template) => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/clienti/${cid}/whatsapp/preview`, { template });
      setPreview({ ...data, _template: template });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  const confirmSend = async () => {
    if (!preview) return;
    setSending(true);
    try {
      const payload = { template: preview._template };
      if (preview.body && preview._template === "custom") payload.body = preview.body;
      if (followupMinutes !== null && followupMinutes !== "") {
        payload.followup_minutes = Number(followupMinutes);
      }
      const r = await api.post(`/clienti/${cid}/whatsapp/send`, payload);
      const created = r?.data?.followup_id;
      toast.success(
        preview.mocked
          ? `Messaggio salvato in modalità MOCK${created ? " · Promemoria chiamata creato" : ""}`
          : `Messaggio inviato${created ? " · Promemoria chiamata creato" : ""}`
      );
      setPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const closePreview = () => setPreview(null);

  // ---------- Render ----------
  const noPhone = !cliente?.telefono;
  const fromNumber = settings?.from_number || "—";
  const mocked = settings?.mocked !== false; // se settings non caricati assumiamo mocked

  return (
    <div className="space-y-4" data-testid="whatsapp-panel">
      {/* Header status */}
      <div className="card-shell p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: "rgba(37,211,102,0.12)" }}>
            <MessageCircle className="w-5 h-5" style={{ color: "#25D366" }} />
          </div>
          <div>
            <div className="font-bold" style={{ fontFamily: "Manrope" }}>Comunicazioni WhatsApp</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Mittente: <span className="font-mono">{fromNumber}</span>
              {mocked && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: "#FEF3C7", color: "#92400E" }}>MOCKED · Twilio non configurato</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ConversationWindowBadge win={windowInfo} />
          <button onClick={load} className="btn-secondary text-xs" disabled={loading} data-testid="wa-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />} Aggiorna
          </button>
        </div>
      </div>

      {/* Send buttons */}
      {canSend && (
        <div className="card-shell p-4">
          <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
            Invio rapido
          </div>
          {noPhone && (
            <div className="mb-3 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Numero di telefono mancante in anagrafica. Aggiungilo per abilitare l'invio.</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => requestPreview("pianificazione")}
              disabled={previewing || noPhone}
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-white font-semibold transition disabled:opacity-50 hover:brightness-105"
              style={{ background: "#25D366" }}
              data-testid="wa-btn-pianificazione"
            >
              <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Invia pianificazione</span>
              <span className="text-[11px] opacity-90">Template standard</span>
            </button>
            <button
              onClick={() => requestPreview("variazione")}
              disabled={previewing || noPhone || !hasVariazione}
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg font-semibold transition disabled:opacity-50 hover:brightness-105"
              style={{ background: hasVariazione ? "#F59E0B" : "#E5E7EB", color: hasVariazione ? "white" : "#9CA3AF" }}
              data-testid="wa-btn-variazione"
              title={hasVariazione ? "" : "Disponibile solo dopo aver cambiato la data dell'appuntamento nella pianificazione"}
            >
              <span className="flex items-center gap-2"><RotateCw className="w-4 h-4" /> Invia variazione</span>
              <span className="text-[11px] opacity-90">{hasVariazione ? "Variazione rilevata" : "Nessuna variazione"}</span>
            </button>
          </div>
        </div>
      )}

      {/* Thread */}
      <div className="card-shell">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-between p-4 border-b text-left"
          style={{ borderColor: "var(--border)" }}
          data-testid="wa-toggle-history"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            <span className="font-bold" style={{ fontFamily: "Manrope" }}>Conversazione</span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--sidebar)", color: "var(--text-muted)" }}>{messages.length}</span>
          </div>
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHistory && (
          <div ref={threadRef} className="p-4 max-h-[480px] overflow-y-auto space-y-3" style={{ background: "#ECE5DD" }} data-testid="wa-thread">
            {messages.length === 0 ? (
              <div className="text-center text-sm py-10" style={{ color: "var(--text-muted)" }}>
                Nessun messaggio ancora. Usa i bottoni sopra per inviarne uno.
              </div>
            ) : (
              messages.map((m) => (
                <Bubble key={m.id} msg={m} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal preview */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={closePreview} data-testid="wa-preview-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
              <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>
                Anteprima · {preview._template === "pianificazione" ? "Pianificazione" : preview._template === "variazione" ? "Variazione appuntamento" : "Messaggio"}
              </h3>
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                Destinatario: <span className="font-mono font-semibold">{preview.to_number || preview.raw_phone || "n/d"}</span>
                {!preview.to_valid && <span className="ml-2 text-red-700">⚠ numero non valido</span>}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div className="p-3 rounded-lg whitespace-pre-wrap text-sm" style={{ background: "#DCF8C6", color: "#0B141A" }} data-testid="wa-preview-body">
                {preview.body}
              </div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                {preview.char_count} caratteri · {preview.mocked ? "Modalità MOCK · il messaggio viene salvato ma non spedito" : "Verrà inviato via Twilio WhatsApp"}
              </div>
              {/* Promemoria chiamata */}
              <div className="rounded-lg border p-3" style={{ borderColor: "var(--border)", background: "rgba(254,243,199,0.4)" }}>
                <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: "#92400E" }}>
                  📞 Promemoria chiamata cliente
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={followupMinutes ?? ""}
                    onChange={(e) => setFollowupMinutes(e.target.value === "" ? null : Number(e.target.value))}
                    className="flex-1 px-3 py-2 rounded-md border text-sm bg-white"
                    style={{ borderColor: "var(--border)" }}
                    data-testid="wa-followup-select"
                  >
                    <option value="">Nessun promemoria</option>
                    <option value="0">Subito (suona appena inviato)</option>
                    <option value="30">Tra 30 minuti</option>
                    <option value="60">Tra 1 ora</option>
                    <option value="120">Tra 2 ore</option>
                    <option value="240">Tra 4 ore</option>
                    <option value="1440">Tra 24 ore</option>
                  </select>
                </div>
                <div className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                  Apparirà nella campanella in alto. Si chiude automaticamente se il cliente risponde su WhatsApp.
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border)" }}>
              <button onClick={closePreview} className="btn-secondary text-sm" disabled={sending} data-testid="wa-preview-cancel">Annulla</button>
              <button onClick={confirmSend} disabled={!preview.to_valid || sending} className="px-4 py-2 rounded-md text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#25D366" }} data-testid="wa-preview-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : <Send className="w-4 h-4 inline-block mr-1" />} Conferma e invia
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ msg }) {
  const out = msg.direction === "out";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-bubble-${msg.id}`}>
      <div className="max-w-[80%]" data-testid={`wa-msg-${out ? "out" : "in"}-${msg.id}`}>
        <div
          className="px-3 py-2 rounded-lg shadow-sm whitespace-pre-wrap text-sm"
          style={{
            background: out ? "#DCF8C6" : "#fff",
            color: "#0B141A",
            borderTopRightRadius: out ? 4 : undefined,
            borderTopLeftRadius: out ? undefined : 4,
          }}
        >
          {msg.body}
          {msg.error && (
            <div className="mt-2 text-[11px] flex items-center gap-1" style={{ color: "#991B1B" }}>
              <AlertTriangle className="w-3 h-3" /> {msg.error}
            </div>
          )}
        </div>
        <div className={`flex items-center gap-2 mt-1 text-[10px] ${out ? "justify-end" : "justify-start"}`} style={{ color: "#5C665D" }} data-testid={`wa-msg-status-${msg.id}`}>
          <span>{fmtTime(msg.created_at)}</span>
          {out && <StatusBadge status={msg.status} />}
          {msg.user_name && out && <span>· {msg.user_name}</span>}
          {msg.mocked && <span className="px-1 py-0 rounded text-[9px] font-bold" style={{ background: "#FEF3C7", color: "#92400E" }}>MOCK</span>}
        </div>
      </div>
    </div>
  );
}

function ConversationWindowBadge({ win }) {
  if (!win) return null;
  const open = !!win.open;
  const bg = open ? "#DCFCE7" : "#FEF3C7";
  const fg = open ? "#166534" : "#92400E";
  const border = open ? "#86EFAC" : "#FCD34D";
  const Icon = open ? ShieldCheck : Hourglass;
  let label;
  if (open) {
    const h = win.hours_remaining || 0;
    const m = win.minutes_remaining || 0;
    label = `Finestra aperta · ${h}h ${m}m`;
  } else {
    label = win.last_inbound_at ? "Finestra chiusa · serve template approvato" : "Nessuna risposta cliente · serve template";
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border"
      style={{ background: bg, color: fg, borderColor: border }}
      title={win.reason || ""}
      data-testid={`wa-window-${open ? "open" : "closed"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
}
